/**
 * Extraordinary and visually stunning level badges
 * Each badge represents a completely unique concept that motivates users to progress
 */

import React, { useEffect, useRef } from 'react';
import styles from './LevelBadges.module.css';

// Badge for Beginner level - Cosmic Seed


export const BeginnerBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        const drawSeed = () => {
            if (!isVisible) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Seed Shape (Improved Oval with a pointed end)
            ctx.beginPath();
            ctx.moveTo(centerX, centerY - size / 2);
            ctx.bezierCurveTo(
                centerX + size / 3, centerY - size / 3,
                centerX + size / 3, centerY + size / 3,
                centerX, centerY + size / 2
            );
            ctx.bezierCurveTo(
                centerX - size / 3, centerY + size / 3,
                centerX - size / 3, centerY - size / 3,
                centerX, centerY - size / 2
            );
            ctx.closePath();

            const gradient = ctx.createLinearGradient(centerX, centerY - size / 2, centerX, centerY + size / 2);
            gradient.addColorStop(0, '#a86b32'); // Darker brown at top
            gradient.addColorStop(1, '#e2a76f'); // Lighter brown at bottom

            ctx.fillStyle = gradient;
            ctx.fill();

            // Subtle shine
            ctx.beginPath();
            ctx.ellipse(centerX - size / 8, centerY - size / 4, size / 6, size / 10, -0.3, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.fill();

            // Seed texture (curved inner line)
            ctx.beginPath();
            ctx.moveTo(centerX - size / 6, centerY - size / 3);
            ctx.quadraticCurveTo(centerX, centerY, centerX + size / 6, centerY + size / 3);
            ctx.strokeStyle = '#5c3d2e';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Tiny Particles (Energy)
            for (let i = 0; i < 5; i++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * size / 2;
                const x = centerX + Math.cos(angle) * distance;
                const y = centerY + Math.sin(angle) * distance;

                ctx.beginPath();
                ctx.arc(x, y, 1.5, 0, 2 * Math.PI);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                ctx.fill();
            }
        };

        if (isVisible) {
            drawSeed();
            const interval = setInterval(drawSeed, 1000);
            return () => clearInterval(interval);
        } else {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, [size, isVisible]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};



export const NoviceBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !isVisible) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const animate = () => {
            if (!isVisible) return;
            time += 0.02; // Slightly faster animation

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Outer Glow Effect
            ctx.shadowBlur = size / 4;
            ctx.shadowColor = 'rgba(79, 172, 254, 0.7)';

            // Outer Orb
            const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, size / 2);
            gradient.addColorStop(0, '#4facfe');
            gradient.addColorStop(1, '#00f2fe');

            ctx.beginPath();
            ctx.arc(centerX, centerY, size / 2.5, 0, 2 * Math.PI);
            ctx.fillStyle = gradient;
            ctx.fill();

            // Reset shadow for inner effects
            ctx.shadowBlur = 0;

            // Inner Swirls
            for (let i = 0; i < 3; i++) {
                const angle = time + (i * Math.PI * 2) / 3;
                const x = centerX + Math.cos(angle) * size / 4;
                const y = centerY + Math.sin(angle) * size / 4;

                const miniGradient = ctx.createRadialGradient(x, y, 0, x, y, size / 8);
                miniGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
                miniGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

                ctx.beginPath();
                ctx.arc(x, y, size / 8, 0, 2 * Math.PI);
                ctx.fillStyle = miniGradient;
                ctx.fill();
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};


// Badge for Intermediate level - Quantum Crystal
export const IntermediateBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    // eslint-disable-next-line
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let rotation = 0;

        const drawCrystal = () => {
            if (!isVisible) return;
            rotation += 0.01;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw crystal
            ctx.save();
            ctx.translate(centerX, centerY);
            ctx.rotate(rotation);

            // Crystal body
            ctx.beginPath();
            ctx.moveTo(0, -size / 2.5);
            ctx.lineTo(-size / 3, 0);
            ctx.lineTo(0, size / 2.5);
            ctx.lineTo(size / 3, 0);
            ctx.closePath();

            const gradient = ctx.createLinearGradient(-size / 3, 0, size / 3, 0);
            gradient.addColorStop(0, '#a17fe0');
            gradient.addColorStop(0.5, '#5d26c1');
            gradient.addColorStop(1, '#a17fe0');
            ctx.fillStyle = gradient;
            ctx.fill();

            // Crystal facets
            ctx.beginPath();
            ctx.moveTo(0, -size / 2.5);
            ctx.lineTo(0, size / 2.5);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.lineWidth = 1;
            ctx.stroke();

            // Energy points
            for (let i = 0; i < 5; i++) {
                const angle = (i * Math.PI * 2) / 5;
                const distance = size / 4 * Math.sin(rotation * 3 + i);
                const x = Math.cos(angle) * distance;
                const y = Math.sin(angle) * distance;

                ctx.beginPath();
                ctx.arc(x, y, 2, 0, 2 * Math.PI);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
            }

            ctx.restore();

            requestAnimationFrame(drawCrystal);
        };

        const animation = requestAnimationFrame(drawCrystal);

        return () => cancelAnimationFrame(animation);
        // eslint-disable-next-line
    }, [size]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Badge for Advanced level - Astral Compass
export const AdvancedBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const drawCompass = () => {
            if (!isVisible) return;
            time += 0.02;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Compass ring
            ctx.beginPath();
            ctx.arc(centerX, centerY, size / 2.5, 0, 2 * Math.PI);
            ctx.strokeStyle = '#f2994a';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Constellation background
            for (let i = 0; i < 20; i++) {
                const angle = (i * Math.PI * 2) / 20;
                const x = centerX + Math.cos(angle) * size / 3;
                const y = centerY + Math.sin(angle) * size / 3;

                ctx.beginPath();
                ctx.arc(x, y, 0.5, 0, 2 * Math.PI);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
            }

            // Compass needles
            const angles = [0, Math.PI / 2, Math.PI, Math.PI * 1.5];

            angles.forEach((baseAngle, i) => {
                const angle = baseAngle + Math.sin(time + i) * 0.2;
                const needleLength = size / 2.5;

                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.lineTo(
                    centerX + Math.cos(angle) * needleLength,
                    centerY + Math.sin(angle) * needleLength
                );

                const gradient = ctx.createLinearGradient(
                    centerX, centerY,
                    centerX + Math.cos(angle) * needleLength,
                    centerY + Math.sin(angle) * needleLength
                );

                gradient.addColorStop(0, '#f2994a');
                gradient.addColorStop(1, '#f2c94c');

                ctx.strokeStyle = gradient;
                ctx.lineWidth = 2;
                ctx.stroke();
            });

            // Center circle
            ctx.beginPath();
            ctx.arc(centerX, centerY, size / 10, 0, 2 * Math.PI);
            ctx.fillStyle = '#f2994a';
            ctx.fill();

            requestAnimationFrame(drawCompass);
        };

        if (isVisible) {
            animationRef.current = requestAnimationFrame(drawCompass);
        } else {
            // Clear canvas when not visible
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);
    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Badge for Proficient level - Phoenix Flame
export const ProficientBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const drawFlame = () => {
            if (!isVisible) return;
            time += 0.05;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Flame effect
            const flameHeight = size / 2;

            for (let i = 0; i < 20; i++) {
                const t = time + i * 0.2;
                const width = size / 3 * (1 - i / 20);
                const height = flameHeight * (1 - i / 20);
                const offsetX = Math.cos(t * 2) * width / 5;

                ctx.beginPath();
                ctx.moveTo(centerX - width / 2 + offsetX, centerY + height / 2);
                ctx.quadraticCurveTo(
                    centerX + offsetX,
                    centerY - height / 2 - Math.sin(t) * height / 4,
                    centerX + width / 2 + offsetX,
                    centerY + height / 2
                );
                ctx.quadraticCurveTo(
                    centerX + offsetX,
                    centerY + height / 2 + height / 4,
                    centerX - width / 2 + offsetX,
                    centerY + height / 2
                );

                const gradient = ctx.createLinearGradient(
                    centerX, centerY + height / 2,
                    centerX, centerY - height / 2
                );

                gradient.addColorStop(0, `rgba(255, 107, 107, ${0.1 + 0.9 * (1 - i / 20)})`);
                gradient.addColorStop(0.5, `rgba(255, 204, 77, ${0.1 + 0.9 * (1 - i / 20)})`);
                gradient.addColorStop(1, `rgba(255, 243, 224, ${0.1 + 0.9 * (1 - i / 20)})`);

                ctx.fillStyle = gradient;
                ctx.fill();
            }

            // Phoenix bird silhouette
            ctx.beginPath();
            ctx.moveTo(centerX, centerY - size / 6);
            ctx.bezierCurveTo(
                centerX + size / 10, centerY - size / 6,
                centerX + size / 6, centerY - size / 10,
                centerX + size / 6, centerY
            );
            ctx.bezierCurveTo(
                centerX + size / 6, centerY + size / 10,
                centerX + size / 10, centerY + size / 6,
                centerX, centerY + size / 6
            );
            ctx.bezierCurveTo(
                centerX - size / 10, centerY + size / 6,
                centerX - size / 6, centerY + size / 10,
                centerX - size / 6, centerY
            );
            ctx.bezierCurveTo(
                centerX - size / 6, centerY - size / 10,
                centerX - size / 10, centerY - size / 6,
                centerX, centerY - size / 6
            );

            ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
            ctx.fill();

            requestAnimationFrame(drawFlame);
        };

        if (isVisible) {
            animationRef.current = requestAnimationFrame(drawFlame);
        } else {
            // Clear canvas when not visible
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Badge for Expert level - Dimensional Gate
export const ExpertBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const drawGate = () => {
            if (!isVisible) return;
            time += 0.02;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Background void
            const bgGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, size);
            bgGradient.addColorStop(0, '#4a00e0');
            bgGradient.addColorStop(0.6, '#2c2c54');
            bgGradient.addColorStop(1, '#17172b');

            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Portal ring
            const ringWidth = size / 12;
            const ringRadius = size / 2.5;

            ctx.beginPath();
            ctx.ellipse(centerX, centerY, ringRadius, ringRadius * 0.6, 0, 0, 2 * Math.PI);
            ctx.strokeStyle = '#c56cf0';
            ctx.lineWidth = ringWidth;
            ctx.stroke();

            // Portal energy waves
            for (let i = 0; i < 3; i++) {
                const waveRadius = ringRadius * (0.7 - i * 0.2);

                ctx.beginPath();
                ctx.ellipse(
                    centerX,
                    centerY,
                    waveRadius,
                    waveRadius * 0.6,
                    0, 0, 2 * Math.PI
                );

                ctx.strokeStyle = `rgba(197, 108, 240, ${0.3 - i * 0.1})`;
                ctx.lineWidth = 1;
                ctx.stroke();
            }

            // Portal energy particles
            for (let i = 0; i < 10; i++) {
                const angle = time + (i * Math.PI * 2) / 10;
                const particleX = centerX + Math.cos(angle) * ringRadius * 0.3;
                const particleY = centerY + Math.sin(angle) * ringRadius * 0.6 * 0.3;

                ctx.beginPath();
                ctx.arc(particleX, particleY, 2, 0, 2 * Math.PI);
                ctx.fillStyle = '#c56cf0';
                ctx.fill();
            }

            // Portal flare
            const flareGradient = ctx.createRadialGradient(
                centerX, centerY, 0,
                centerX, centerY, ringRadius * 0.6
            );
            flareGradient.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
            flareGradient.addColorStop(0.3, 'rgba(197, 108, 240, 0.2)');
            flareGradient.addColorStop(1, 'rgba(74, 0, 224, 0)');

            ctx.beginPath();
            ctx.ellipse(centerX, centerY, ringRadius * 0.6, ringRadius * 0.6 * 0.6, 0, 0, 2 * Math.PI);
            ctx.fillStyle = flareGradient;
            ctx.fill();

            // Rune symbols
            const numberOfRunes = 6;
            for (let i = 0; i < numberOfRunes; i++) {
                const runeAngle = (i * Math.PI * 2) / numberOfRunes;
                const runeX = centerX + Math.cos(runeAngle) * (ringRadius + ringWidth / 2);
                const runeY = centerY + Math.sin(runeAngle) * (ringRadius + ringWidth / 2) * 0.6;

                ctx.save();
                ctx.translate(runeX, runeY);
                ctx.rotate(runeAngle);

                // Draw a simple rune
                ctx.beginPath();
                ctx.moveTo(-3, -3);
                ctx.lineTo(3, 3);
                ctx.moveTo(-3, 3);
                ctx.lineTo(3, -3);

                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 1;
                ctx.stroke();

                ctx.restore();
            }

            requestAnimationFrame(drawGate);
        };
        if (isVisible) {
            animationRef.current = requestAnimationFrame(drawGate);
        } else {
            // Clear canvas when not visible
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Badge for Elite level - Celestial Clock
export const EliteBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const drawClock = () => {
            if (!isVisible) return;
            time += 0.01;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Outer ring
            ctx.beginPath();
            ctx.arc(centerX, centerY, size / 2.3, 0, 2 * Math.PI);
            const ringGradient = ctx.createLinearGradient(
                centerX - size / 2, centerY, centerX + size / 2, centerY
            );
            ringGradient.addColorStop(0, '#8E2DE2');
            ringGradient.addColorStop(1, '#4A00E0');
            ctx.strokeStyle = ringGradient;
            ctx.lineWidth = 3;
            ctx.stroke();

            // Clock face
            ctx.beginPath();
            ctx.arc(centerX, centerY, size / 2.8, 0, 2 * Math.PI);
            const faceGradient = ctx.createRadialGradient(
                centerX, centerY, 0, centerX, centerY, size / 2.8
            );
            faceGradient.addColorStop(0, '#141e30');
            faceGradient.addColorStop(1, '#243b55');
            ctx.fillStyle = faceGradient;
            ctx.fill();

            // Star pattern
            const stars = 12;
            for (let i = 0; i < stars; i++) {
                const starAngle = (i * Math.PI * 2) / stars;
                const starX = centerX + Math.cos(starAngle) * size / 3.5;
                const starY = centerY + Math.sin(starAngle) * size / 3.5;

                // Draw a star
                ctx.beginPath();
                for (let j = 0; j < 5; j++) {
                    const angle = starAngle + (j * Math.PI * 2) / 5 - Math.PI / 2;
                    const length = j % 2 === 0 ? size / 20 : size / 40;
                    const x = starX + Math.cos(angle) * length;
                    const y = starY + Math.sin(angle) * length;

                    if (j === 0) {
                        ctx.moveTo(x, y);
                    } else {
                        ctx.lineTo(x, y);
                    }
                }
                ctx.closePath();
                ctx.fillStyle = '#f5f5f5';
                ctx.fill();
            }

            // Constellation lines
            ctx.beginPath();
            for (let i = 0; i < stars; i++) {
                const starAngle1 = (i * Math.PI * 2) / stars;
                const starAngle2 = ((i + 1) % stars * Math.PI * 2) / stars;
                const starX1 = centerX + Math.cos(starAngle1) * size / 3.5;
                const starY1 = centerY + Math.sin(starAngle1) * size / 3.5;
                const starX2 = centerX + Math.cos(starAngle2) * size / 3.5;
                const starY2 = centerY + Math.sin(starAngle2) * size / 3.5;

                ctx.moveTo(starX1, starY1);
                ctx.lineTo(starX2, starY2);
            }
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
            ctx.lineWidth = 1;
            ctx.stroke();

            // Clock hands
            const handAngles = [
                time * 0.5, // Hour hand
                time * 2,   // Minute hand
                time * 8    // Second hand
            ];

            const handLengths = [size / 5, size / 3.8, size / 3.5];
            const handColors = ['#f9ca24', '#f0932b', '#eb4d4b'];

            handAngles.forEach((angle, index) => {
                const handX = centerX + Math.cos(angle) * handLengths[index];
                const handY = centerY + Math.sin(angle) * handLengths[index];

                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.lineTo(handX, handY);
                ctx.strokeStyle = handColors[index];
                ctx.lineWidth = 3 - index * 0.5;
                ctx.stroke();
            });

            // Center disk
            ctx.beginPath();
            ctx.arc(centerX, centerY, size / 15, 0, 2 * Math.PI);
            ctx.fillStyle = '#f9ca24';
            ctx.fill();

            requestAnimationFrame(drawClock);
        };

        if (isVisible) {
            animationRef.current = requestAnimationFrame(drawClock);
        } else {
            // Clear canvas when not visible
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Badge for Master level - Cosmic Tree
export const MasterBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const drawTree = () => {
            if (!isVisible) return;
            time += 0.02;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Background
            const gradient = ctx.createRadialGradient(
                centerX, centerY, 0, centerX, centerY, size
            );
            gradient.addColorStop(0, '#182848');
            gradient.addColorStop(1, '#0b0b19');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Tree roots (recursive function)
            const drawBranch = (x, y, length, angle, depth, width) => {
                if (depth === 0) return;

                const endX = x + Math.cos(angle) * length;
                const endY = y + Math.sin(angle) * length;

                // Draw branch
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(endX, endY);

                // Color gradient based on depth
                const branchGradient = ctx.createLinearGradient(x, y, endX, endY);

                if (depth > 3) {
                    branchGradient.addColorStop(0, '#6a3093');
                    branchGradient.addColorStop(1, '#4a00e0');
                } else {
                    branchGradient.addColorStop(0, '#8e44ad');
                    branchGradient.addColorStop(1, '#c7ecee');
                }

                ctx.strokeStyle = branchGradient;
                ctx.lineWidth = width;
                ctx.stroke();

                // Draw leaf/energy point at end of small branches
                if (depth <= 2) {
                    ctx.beginPath();
                    ctx.arc(endX, endY, width * 2, 0, 2 * Math.PI);
                    ctx.fillStyle = '#c7ecee';
                    ctx.fill();

                    // Add glow
                    ctx.beginPath();
                    ctx.arc(endX, endY, width * 4, 0, 2 * Math.PI);
                    const glowGradient = ctx.createRadialGradient(
                        endX, endY, 0, endX, endY, width * 4
                    );
                    glowGradient.addColorStop(0, 'rgba(199, 236, 238, 0.5)');
                    glowGradient.addColorStop(1, 'rgba(199, 236, 238, 0)');
                    ctx.fillStyle = glowGradient;
                    ctx.fill();
                }

                // Recursively draw branches
                const branchFactor = 0.7;
                const angleChange = Math.PI / 4 + Math.sin(time) * 0.1;

                drawBranch(
                    endX, endY,
                    length * branchFactor,
                    angle - angleChange,
                    depth - 1,
                    width * 0.7
                );

                drawBranch(
                    endX, endY,
                    length * branchFactor,
                    angle + angleChange,
                    depth - 1,
                    width * 0.7
                );
            };

            // Start the recursive tree drawing
            drawBranch(
                centerX, centerY + size / 3, // Start at bottom center
                size / 4,                    // Initial branch length
                -Math.PI / 2,                // Initial angle (up)
                5,                         // Depth
                3                          // Initial line width
            );

            requestAnimationFrame(drawTree);
        };

        if (isVisible) {
            animationRef.current = requestAnimationFrame(drawTree);
        } else {
            // Clear canvas when not visible
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Badge for Grandmaster level - Nebula Core
export const GrandmasterBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const drawNebula = () => {
            if (!isVisible) return;
            time += 0.01;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Universe background
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Star field
            for (let i = 0; i < 50; i++) {
                const x = Math.random() * canvas.width;
                const y = Math.random() * canvas.height;
                const radius = Math.random() * 1.5;

                ctx.beginPath();
                ctx.arc(x, y, radius, 0, 2 * Math.PI);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                ctx.fill();
            }

            // Nebula gas clouds
            const cloudColors = [
                'rgba(255, 0, 128, 0.2)',
                'rgba(0, 191, 255, 0.2)',
                'rgba(138, 43, 226, 0.2)',
            ];

            for (let c = 0; c < cloudColors.length; c++) {
                for (let i = 0; i < 6; i++) {
                    const cloudRadius = size / 3 + Math.sin(time + i) * size / 10;
                    const offsetX = Math.cos(time * 0.5 + i * 0.7) * size / 5;
                    const offsetY = Math.sin(time * 0.3 + i * 0.5) * size / 5;

                    ctx.beginPath();
                    ctx.arc(centerX + offsetX, centerY + offsetY, cloudRadius, 0, 2 * Math.PI);
                    ctx.fillStyle = cloudColors[c];
                    ctx.fill();
                }
            }

            // Core particles
            const particleCount = 20;
            for (let i = 0; i < particleCount; i++) {
                const angle = (i * Math.PI * 2) / particleCount + time;
                const distance = size / 6 * (1 + Math.sin(time * 3 + i) * 0.2);
                const x = centerX + Math.cos(angle) * distance;
                const y = centerY + Math.sin(angle) * distance;

                ctx.beginPath();
                ctx.arc(x, y, 2, 0, 2 * Math.PI);

                // Cycle through colors
                const colorIndex = (i + Math.floor(time * 5)) % 3;
                const particleColors = ['#ff0080', '#00bfff', '#8a2be2'];
                ctx.fillStyle = particleColors[colorIndex];
                ctx.fill();

                // Connect particles in orbit
                if (i > 0) {
                    const prevAngle = ((i - 1) * Math.PI * 2) / particleCount + time;
                    const prevDistance = size / 6 * (1 + Math.sin(time * 3 + (i - 1)) * 0.2);
                    const prevX = centerX + Math.cos(prevAngle) * prevDistance;
                    const prevY = centerY + Math.sin(prevAngle) * prevDistance;

                    ctx.beginPath();
                    ctx.moveTo(prevX, prevY);
                    ctx.lineTo(x, y);
                    ctx.strokeStyle = `rgba(255, 255, 255, 0.3)`;
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }
            }

            // Pulsing core
            const coreRadius = size / 10 * (1 + Math.sin(time * 5) * 0.2);
            const coreGradient = ctx.createRadialGradient(
                centerX, centerY, 0,
                centerX, centerY, coreRadius * 2
            );
            coreGradient.addColorStop(0, 'white');
            coreGradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.8)');
            coreGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

            ctx.beginPath();
            ctx.arc(centerX, centerY, coreRadius * 2, 0, 2 * Math.PI);
            ctx.fillStyle = coreGradient;
            ctx.fill();

            requestAnimationFrame(drawNebula);
        };

        if (isVisible) {
            animationRef.current = requestAnimationFrame(drawNebula);
        } else {
            // Clear canvas when not visible
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);

    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Badge for Legendary level - Cosmic Architect
export const LegendaryBadge = ({ size = 40, isVisible = true }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        let time = 0;

        const drawArchitect = () => {
            if (!isVisible) return;
            time += 0.01;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Space-time fabric background
            const gridSize = 10;
            const gridSpacing = size / gridSize;

            // Draw distorted grid
            for (let x = 0; x <= gridSize; x++) {
                for (let y = 0; y <= gridSize; y++) {
                    const baseX = x * gridSpacing;
                    const baseY = y * gridSpacing;

                    // Calculate distortion based on distance from center
                    const dx = baseX - centerX;
                    const dy = baseY - centerY;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const angle = Math.atan2(dy, dx) + time * 0.2;

                    const distortion = Math.sin(distance / 10 - time * 3) * 5;

                    const distortedX = baseX + Math.cos(angle) * distortion;
                    const distortedY = baseY + Math.sin(angle) * distortion;

                    if (x < gridSize) {
                        const nextX = (x + 1) * gridSpacing;
                        const nextDx = nextX - centerX;
                        const nextAngle = Math.atan2(dy, nextDx) + time * 0.2;
                        const nextDistortion = Math.sin(Math.sqrt(nextDx * nextDx + dy * dy) / 10 - time * 3) * 5;
                        const nextDistortedX = nextX + Math.cos(nextAngle) * nextDistortion;

                        ctx.beginPath();
                        ctx.moveTo(distortedX, distortedY);
                        ctx.lineTo(nextDistortedX, distortedY);
                        ctx.strokeStyle = `rgba(255, 215, 0, ${0.3 - distance / size})`;
                        ctx.lineWidth = 1;
                        ctx.stroke();
                    }

                    if (y < gridSize) {
                        const nextY = (y + 1) * gridSpacing;
                        const nextDy = nextY - centerY;
                        const nextAngle = Math.atan2(nextDy, dx) + time * 0.2;
                        const nextDistortion = Math.sin(Math.sqrt(dx * dx + nextDy * nextDy) / 10 - time * 3) * 5;
                        const nextDistortedY = nextY + Math.sin(nextAngle) * nextDistortion;

                        ctx.beginPath();
                        ctx.moveTo(distortedX, distortedY);
                        ctx.lineTo(distortedX, nextDistortedY);
                        ctx.strokeStyle = `rgba(255, 215, 0, ${0.3 - distance / size})`;
                        ctx.lineWidth = 1;
                        ctx.stroke();
                    }
                }
            }

            // Sacred geometry - Metatron's Cube
            const points = [];
            const numPoints = 10;
            const radius = size / 3;

            // Generate points around a circle
            for (let i = 0; i < numPoints; i++) {
                const angle = (i * Math.PI * 2) / numPoints;
                points.push({
                    x: centerX + Math.cos(angle) * radius,
                    y: centerY + Math.sin(angle) * radius
                });
            }

            // Add center point
            points.push({ x: centerX, y: centerY });

            // Connect all points to each other
            for (let i = 0; i < points.length; i++) {
                for (let j = i + 1; j < points.length; j++) {
                    ctx.beginPath();
                    ctx.moveTo(points[i].x, points[i].y);
                    ctx.lineTo(points[j].x, points[j].y);
                    ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }
            }

            // Draw golden energy points
            for (let i = 0; i < points.length; i++) {
                ctx.beginPath();
                const pulseSize = 3 + Math.sin(time * 3 + i) * 2;
                ctx.arc(points[i].x, points[i].y, pulseSize, 0, 2 * Math.PI);
                ctx.fillStyle = 'rgba(255, 215, 0, 0.8)';
                ctx.fill();

                // Add glow
                ctx.beginPath();
                ctx.arc(points[i].x, points[i].y, pulseSize * 2, 0, 2 * Math.PI);
                const glowGradient = ctx.createRadialGradient(
                    points[i].x, points[i].y, 0,
                    points[i].x, points[i].y, pulseSize * 3
                );
                glowGradient.addColorStop(0, 'rgba(255, 215, 0, 0.5)');
                glowGradient.addColorStop(1, 'rgba(255, 215, 0, 0)');
                ctx.fillStyle = glowGradient;
                ctx.fill();
            }

            // Central orb
            ctx.beginPath();
            ctx.arc(centerX, centerY, size / 8, 0, 2 * Math.PI);
            const orbGradient = ctx.createRadialGradient(
                centerX, centerY, 0,
                centerX, centerY, size / 8
            );
            orbGradient.addColorStop(0, 'white');
            orbGradient.addColorStop(0.5, 'rgba(255, 215, 0, 0.8)');
            orbGradient.addColorStop(1, 'rgba(255, 215, 0, 0.4)');
            ctx.fillStyle = orbGradient;
            ctx.fill();

            // Energy beams from orb
            const beamCount = 6;
            for (let i = 0; i < beamCount; i++) {
                const beamAngle = (i * Math.PI * 2) / beamCount + time;
                const endX = centerX + Math.cos(beamAngle) * size / 1.5;
                const endY = centerY + Math.sin(beamAngle) * size / 1.5;

                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.lineTo(endX, endY);

                const beamGradient = ctx.createLinearGradient(
                    centerX, centerY, endX, endY
                );
                beamGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
                beamGradient.addColorStop(0.5, 'rgba(255, 215, 0, 0.5)');
                beamGradient.addColorStop(1, 'rgba(255, 215, 0, 0)');

                ctx.strokeStyle = beamGradient;
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            requestAnimationFrame(drawArchitect);
        };

        if (isVisible) {
            animationRef.current = requestAnimationFrame(drawArchitect);
        } else {
            // Clear canvas when not visible
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [size, isVisible]);
    return (
        <div className={styles.badgeWrapper} style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * 2} height={size * 2} className={styles.badgeCanvas} />
        </div>
    );
};

// Get the appropriate badge component for a level name
export const getLevelBadge = (levelName, size = 40, isVisible = true) => {
    switch (levelName) {
        case 'Beginner':
            return <BeginnerBadge size={size} isVisible={isVisible} />;
        case 'Novice':
            return <NoviceBadge size={size} isVisible={isVisible} />;
        case 'Intermediate':
            return <IntermediateBadge size={size} isVisible={isVisible} />;
        case 'Advanced':
            return <AdvancedBadge size={size} isVisible={isVisible} />;
        case 'Proficient':
            return <ProficientBadge size={size} isVisible={isVisible} />;
        case 'Expert':
            return <ExpertBadge size={size} isVisible={isVisible} />;
        case 'Elite':
            return <EliteBadge size={size} isVisible={isVisible} />;
        case 'Master':
            return <MasterBadge size={size} isVisible={isVisible} />;
        case 'Grandmaster':
            return <GrandmasterBadge size={size} isVisible={isVisible} />;
        case 'Legendary':
            return <LegendaryBadge size={size} isVisible={isVisible} />;
        default:
            return <BeginnerBadge size={size} isVisible={isVisible} />;
    }
};
// eslint-disable-next-line
export default {
    BeginnerBadge,
    NoviceBadge,
    IntermediateBadge,
    AdvancedBadge,
    ProficientBadge,
    ExpertBadge,
    EliteBadge,
    MasterBadge,
    GrandmasterBadge,
    LegendaryBadge,
    getLevelBadge
};